import React from 'react';

const Contact: React.FC = () => {
  return (
    <section id="contact" className="py-16 md:py-32 bg-gradient-to-br from-prussian-blue to-lapis-lazuli relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-72 h-72 bg-celestial-blue/10 rounded-full blur-3xl smooth-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-alice-blue/10 rounded-full blur-3xl smooth-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 text-center relative z-10 max-w-6xl">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center bg-alice-blue/20 border border-alice-blue/30 text-alice-blue px-4 md:px-8 py-2 md:py-4 rounded-full text-xs md:text-sm font-medium mb-6 md:mb-10 fade-in">
            ابدئي رحلتكِ اليوم
          </div>
          
          <h2 className="text-3xl md:text-6xl lg:text-7xl font-black text-white mb-6 md:mb-10 leading-tight fade-in fade-in-delay-1">
            هل أنتِ مستعدة؟<span className="text-celestial-blue">.</span>
          </h2>
          
          <p className="text-lg md:text-2xl text-alice-blue/90 mb-8 md:mb-16 max-w-4xl mx-auto leading-relaxed px-4 fade-in fade-in-delay-2">
            الخطوة الأولى هي الأصعب، ونحن هنا لنجعلها أسهل ما يمكن. تواصلي معنا لبدء رحلتكِ نحو النجاح.
          </p>

          {/* Contact methods */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-10 mb-8 md:mb-16 max-w-5xl mx-auto">
            {[
              { icon: '📞', title: 'اتصلي بنا', desc: 'احجزي موعدكِ الآن' },
              { 
                icon: (
                  <div className="flex justify-center">
                    <svg className="w-12 h-12 md:w-16 md:h-16" viewBox="0 0 175.216 175.552">
                      <defs>
                        <linearGradient id="whatsapp-gradient" x1="85.915" x2="86.535" y1="32.567" y2="137.092" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#57d163"/>
                          <stop offset="1" stopColor="#23b33a"/>
                        </linearGradient>
                        <filter id="whatsapp-filter" width="1.115" height="1.114" x="-.057" y="-.057" colorInterpolationFilters="sRGB">
                          <feGaussianBlur stdDeviation="3.531"/>
                        </filter>
                      </defs>
                      <path fill="#fff" d="M87.184 25.227c-33.733 0-61.166 27.423-61.178 61.13a60.98 60.98 0 0 0 9.349 32.535l1.455 2.313-6.179 22.558 23.146-6.069 2.235 1.324c9.387 5.571 20.15 8.517 31.126 8.523h.023c33.707 0 61.14-27.426 61.153-61.135a60.75 60.75 0 0 0-17.895-43.251 60.75 60.75 0 0 0-43.235-17.928z"/>
                      <path fill="url(#whatsapp-gradient)" d="M87.184 25.227c-33.733 0-61.166 27.423-61.178 61.13a60.98 60.98 0 0 0 9.349 32.535l1.455 2.313-6.179 22.558 23.146-6.069 2.235 1.324c9.387 5.571 20.15 8.517 31.126 8.523h.023c33.707 0 61.14-27.426 61.153-61.135a60.75 60.75 0 0 0-17.895-43.251 60.75 60.75 0 0 0-43.235-17.928z"/>
                      <path fill="#fff" fillRule="evenodd" d="M68.772 55.603c-1.378-3.061-2.828-3.123-4.137-3.176l-3.524-.043c-1.226 0-3.218.46-4.902 2.3s-6.435 6.287-6.435 15.332 6.588 17.785 7.506 19.013 12.718 20.381 31.405 27.75c15.529 6.124 18.689 4.906 22.061 4.6s10.877-4.447 12.408-8.74 1.532-7.971 1.073-8.74-1.685-1.226-3.525-2.146-10.877-5.367-12.562-5.981-2.91-.919-4.137.921-4.746 5.979-5.819 7.206-2.144 1.381-3.984.462-7.76-2.861-14.784-9.124c-5.465-4.873-9.154-10.891-10.228-12.73s-.114-2.835.808-3.751c.825-.824 1.838-2.147 2.759-3.22s1.224-1.84 1.836-3.065.307-2.301-.153-3.22-4.032-10.011-5.666-13.647"/>
                    </svg>
                  </div>
                ), 
                title: 'واتساب', 
                desc: 'تواصلي معنا مباشرة' 
              },
              { icon: '📍', title: 'زيارة الموقع', desc: 'تعرفي على مرافقنا' }
            ].map((contact, index) => (
              <div 
                key={index}
                className={`bg-alice-blue/10 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-10 border border-alice-blue/20 hover:bg-alice-blue/15 transition-all duration-500 group cursor-pointer scale-hover fade-in`}
                style={{animationDelay: `${(index + 3) * 0.2}s`}}
              >
                <div className="text-3xl md:text-5xl mb-3 md:mb-6 transform transition-all duration-300 group-hover:scale-110 flex justify-center">
                  {typeof contact.icon === 'string' ? contact.icon : contact.icon}
                </div>
                <h3 className="text-lg md:text-2xl font-bold text-white mb-2 md:mb-4 group-hover:text-alice-blue transition-colors duration-300">
                  {contact.title}
                </h3>
                <p className="text-alice-blue/80 text-xs md:text-base group-hover:text-white transition-colors duration-300">
                  {contact.desc}
                </p>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 md:gap-8 justify-center fade-in fade-in-delay-4">
            <a 
              href="https://wa.me/971509374446" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-celestial-blue hover:bg-celestial-blue/90 text-white font-bold py-3 md:py-5 px-6 md:px-12 rounded-full text-base md:text-xl transition-all duration-300 scale-hover shadow-2xl hover:shadow-celestial-blue/50 inline-flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 175.216 175.552" fill="currentColor">
                <defs>
                  <linearGradient id="whatsapp-btn-gradient" x1="85.915" x2="86.535" y1="32.567" y2="137.092" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#ffffff"/>
                    <stop offset="1" stopColor="#ffffff"/>
                  </linearGradient>
                </defs>
                <path fill="url(#whatsapp-btn-gradient)" d="M87.184 25.227c-33.733 0-61.166 27.423-61.178 61.13a60.98 60.98 0 0 0 9.349 32.535l1.455 2.313-6.179 22.558 23.146-6.069 2.235 1.324c9.387 5.571 20.15 8.517 31.126 8.523h.023c33.707 0 61.14-27.426 61.153-61.135a60.75 60.75 0 0 0-17.895-43.251 60.75 60.75 0 0 0-43.235-17.928z"/>
              </svg>
              تواصلي عبر الواتساب
            </a>
            <a 
              href="tel:+971509374446"
              className="border-2 border-alice-blue/30 text-alice-blue hover:bg-alice-blue/10 font-bold py-3 md:py-5 px-6 md:px-12 rounded-full text-base md:text-xl transition-all duration-300 scale-hover hover:border-alice-blue/60"
            >
              اتصلي بنا الآن
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 